/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `PasswordRecovery` for the Caintra Connect frontend.
 * @module pages/PasswordRecovery
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../utils/logger.js";
import { apiMessageOrGeneric } from "../utils/genericErrorMessages.js";
import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import styles from "../styles/PasswordRecovery.module.css";
import Caintra_Logo from "../assets/Caintra_Logo.png";
import { ReactComponent as MailIcon } from "../assets/icons/Mail.svg";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import ChangePasswordModal from "../modals/ChangePasswordModal/index.jsx";
import { SvgIcon } from "@mui/material";

const TABLET_BREAKPOINT_PX = 1023;

const PasswordRecovery = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);
  const [values, setValues] = useState({
    email: "",
    showError: false,
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTabletOrMobile(width <= TABLET_BREAKPOINT_PX);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleInputChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleNavigateHome = useCallback(() => navigate("/"), [navigate]);
  const handleModalClose = useCallback(() => setModalOpen(false), []);
  const handleModalChangePassword = useCallback(
    () => logger.info("changePassword"),
    [],
  );

  const sendCode = async () => {
    const authParams = {
      email: values.email,
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(authParams),
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}auth/password/forgot`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(apiMessageOrGeneric(data?.message));
      }
      setValues({ ...values, showError: false });

      if (isTabletOrMobile) {
        navigate("/changePassword", { state: { userEmail: values.email } });
      } else {
        setModalOpen(true);
      }
    } catch (error) {
      logger.info("error: ", { data: error });
      setValues({ ...values, showError: true });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <LanguageSelector />
      </div>
      <div className={styles.imgContainer}>
        <img src={Caintra_Logo} alt="" />
      </div>
      <div className={styles.mainContainer}>
        <div>
          <span className={styles.title}>
            {t("pages.forgotPassword.header")}
          </span>
          <div className={styles.titleDivider}></div>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.textContainer}>
            {isTabletOrMobile ? (
              <span className={styles.subtitle}>
                {t("pages.forgotPassword.subtitle01")}{" "}
                {t("pages.forgotPassword.subtitle02")}
              </span>
            ) : (
              <>
                <span className={styles.subtitle}>
                  {t("pages.forgotPassword.subtitle01")}
                </span>
                <span className={styles.subtitle}>
                  {t("pages.forgotPassword.subtitle02")}
                </span>
              </>
            )}
          </div>
          <div className={styles.inputGroup}>
            <span className={styles.subtitle}>
              {t("pages.forgotPassword.email")}
            </span>
            <div className={styles.inputContainer}>
              <div className={styles.iconContainer}>
                <SvgIcon
                  sx={{ fontSize: "16px", color: "#909092" }}
                  component={MailIcon}
                />
              </div>
              <input
                type="text"
                placeholder={t("pages.forgotPassword.emailPlaceholder")}
                onChange={handleInputChange("email")}
                value={values.email}
              />
            </div>
            {values.showError && (
              <div className={styles.messageContainer}>
                <span className={styles.errorMessage}>
                  {t("errors.forgotPassword.USER_NOT_FOUND")}
                </span>
              </div>
            )}
          </div>
          <div className={styles.buttonContainer}>
            <button
              className={styles.cancelButton}
              type="button"
              onClick={handleNavigateHome}
            >
              {t("pages.forgotPassword.cancel")}
            </button>
            <button
              className={styles.submitButton}
              type="submit"
              onClick={sendCode}
              disabled={!values.email}
            >
              {t("pages.forgotPassword.sendButton")}
            </button>
          </div>
          {modalOpen && (
            <ChangePasswordModal
              open={modalOpen}
              onClose={handleModalClose}
              onChangePassword={handleModalChangePassword}
              userEmail={values.email}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordRecovery;
